import 'whatwg-fetch';
import 'intersection-observer';
import 'ie-array-find-polyfill';
import 'core-js/features/array/from';
import 'core-js/features/array/includes';

import Highway from '@dogstudio/highway';
import { TweenMax } from 'gsap';
import { throttle, debounce } from 'throttle-debounce';
import quicklink from 'quicklink/dist/quicklink.mjs';

// Utils
import emergence from './util/emergence';
import Menu from './util/Menu';
import IE11PolyFills from './util/IE11PolyFills';

// Routes
import Home from './routes/Home';
import Page from './routes/Page';

// Transitions
import Fade from './transitions/Fade';

export default class App {

  /**
   * Loads the page
   * then calls `this.start()` when DOM is ready
   */
  constructor() {
    this.iePolyfills = new IE11PolyFills();
    this.resize = this.resize.bind(this);
    this.scroll = this.scroll.bind(this);
    this.update = this.update.bind(this);

    this.raf = null;

    this.resizeDebounced = debounce(100, this.resize);
    this.resizeThrottled = throttle(150, this.resize);
    this.scrollDebounced = debounce(100, this.scroll);
    this.scrollThrottled = throttle(50, this.scroll);

    this.start();
    this.mobileMenu();
  }

  /**
   * Inits everything that is app-wide
   * ie: Menu, Emergence, scroll / resize events...
   *
   * @returns {void}
   */
  start() {
    this.menu = new Menu();

    this.initHighway().then(() => {
      this.events();

      TweenMax.fromTo(
        document.querySelector('.app'),
        0.5,
        { alpha: 0 },
        {
          alpha: 1,
          delay: 1,
          onStart: () => {
            window.scrollTo(0, 0);
            this.raf = requestAnimationFrame(this.update);
          },
          onComplete: () => {
            this.initEmergence();
          }
        }
      );
    });
  }

  /**
   * Starts an Highway instance
   * with our Views and Transitions
   *
   * @returns {void}
   */
  initHighway() {
    return new Promise((resolve) => {
      this.H = new Highway.Core({
        renderers: {
          home: Home,
          page: Page
        },
        transitions: { default: Fade }
      });

      this.setCurrentRenderer().then(resolve);
    });
  }

  /**
   * Starts an Emergence instance
   *
   * NOTE: `offsetTop` is approximatively the menu/header bar height
   *
   * @returns {void}
   */
  initEmergence() {
    emergence.init({
      reset: false,
      elemCushion: 0.25,
      offsetTop: 80,
      callback: (element, state) => {
        if (!element.animated && state === 'visible') {
          if (element.classList.contains('title-animation')) {
            this.currentRenderer.playTitleAnimation(
              element.dataset.animationIndex
            );
            element.animated = true;
          }
        }
      }
    });

    emergence.engage();
  }

  setCurrentRenderer() {
    return new Promise((resolve) => {
      this.H.properties.renderer.then(() => {
        this.currentRenderer = this.H.To ? this.H.To : this.H.From;

        resolve(this.currentRenderer);
      });
    });
  }

  resize() {
    this.currentRenderer.resize();
    this.menu && this.menu.resize();
  }

  scroll() {
    this.currentRenderer.scroll();
    this.menu && this.menu.scroll();
  }

  update() {
    this.currentRenderer.update();
    this.raf = requestAnimationFrame(this.update);
  }

  events() {
    window.addEventListener('resize', this.resizeThrottled);
    window.addEventListener('resize', this.resizeDebounced);
    window.addEventListener('scroll', this.scrollThrottled);
    window.addEventListener('scroll', this.scrollDebounced);
    window.addEventListener('orientationchange', this.resize);

    this.H.on('NAVIGATE_IN', () => {
      this.setCurrentRenderer().then((renderer) => {
        document.title = renderer.properties.page.title;
      });
    });

    this.H.on('NAVIGATE_END', ({ to, location }) => {
      this.checkAnchor(location);
      emergence.engage();
      quicklink({ el: to.view });
    });

    window.addEventListener('highwayredirect', (e) => {
      this.H.redirect(e.detail.url);
    });
  }

  checkAnchor(location) {
    if (location.anchor) {
      const el = document.querySelector(location.anchor);

      if (el) window.scrollTo(el.offsetLeft, el.offsetTop);
    }
  }

  mobileMenu() {
    const mobileBtn = document.querySelector('.mobile-btn');
    const nav = document.querySelector('nav');
    const menuItems = nav.querySelectorAll('.link');

    mobileBtn.addEventListener('click', () => {
      nav.classList.toggle('open');
    })

    Array.from(menuItems).forEach((item) => {
      item.addEventListener('click', () => {
        if (nav.classList.contains('open')) {
          nav.classList.remove('open');
        }
      })
    })
  }
}



// WEBPACK FOOTER //
// ./scripts/App.js